/** Thin API layer for the ELPM Creator door (library, recordings, references). */
import axios from "axios";
import { getUserToken } from "@/lib/userToken";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUNO_HOSTS = /^(?:cdn\d?\.suno\.ai|audiopipe\.suno\.ai|(?:files|cdn)\.sunoapi\.(?:org|com)|tempfile\.aiquickdraw\.com|musicfile\.aiquickdraw\.com)$/i;

/** Suno CDN links expire / miss CORS — route them through our proxy. */
export function playableUrl(rawUrl) {
  if (!rawUrl) return rawUrl;
  try {
    if (SUNO_HOSTS.test(new URL(rawUrl).hostname)) {
      return `${API}/suno/proxy_url?u=${encodeURIComponent(rawUrl)}`;
    }
  } catch (e) { void e; }
  return rawUrl;
}

export function coverSrc(song) {
  const u = song?.cover_url;
  if (!u) return null;
  return u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export async function listSongs({ deleted = false, limit = 100 } = {}) {
  const { data } = await axios.get(`${API}/library/songs`, {
    params: { user_token: getUserToken(), limit, deleted: deleted ? "true" : "false" },
  });
  return data;
}

export async function getSong(id) {
  const { data } = await axios.get(`${API}/library/songs/${id}`);
  return data;
}

export async function saveSong(body) {
  const { data } = await axios.post(`${API}/library/songs`, { user_token: getUserToken(), ...body });
  return data;
}

export async function patchSong(id, body) {
  const { data } = await axios.patch(`${API}/library/songs/${id}`, body);
  return data;
}

export async function deleteSong(id, { hard = false } = {}) {
  const { data } = await axios.delete(`${API}/library/songs/${id}`, {
    params: { user_token: getUserToken(), hard: hard ? "true" : "false" },
  });
  return data;
}

export async function restoreSong(id) {
  const { data } = await axios.post(`${API}/library/songs/${id}/restore`, null, { params: { user_token: getUserToken() } });
  return data;
}

export async function requestCover(id) {
  const { data } = await axios.post(`${API}/library/songs/${id}/cover`, null, { params: { user_token: getUserToken() } });
  return data;
}

export async function listRecordings() {
  const { data } = await axios.get(`${API}/recording/sessions`, { withCredentials: true });
  return Array.isArray(data) ? data : [];
}

export async function deleteRecording(sessionId) {
  const { data } = await axios.delete(`${API}/recording/session/${sessionId}`, { withCredentials: true });
  return data;
}

/** Upload a reference track for "sound like this" generation → public URL Suno can fetch. */
export async function uploadReferenceFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/suno/upload_reference`, fd, {
    headers: { "X-User-Token": getUserToken() },
    withCredentials: true,
  });
  return `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
}

/** Fetch a URL as a Blob and trigger a browser download. */
export async function downloadUrl(url, filename) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  downloadBlob(await res.blob(), filename);
}

export function downloadBlob(blob, filename) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(href), 1500);
}

export function safeFilename(title, ext) {
  const base = (title || "ELPM song").replace(/[^\w\s-]+/g, "").trim().replace(/\s+/g, " ").slice(0, 60) || "ELPM song";
  return `${base}.${ext}`;
}

/**
 * Fetch + decode + load a URL onto an engine channel WITHOUT awaiting
 * `ctx.resume()` — Chrome keeps that promise pending until a user gesture,
 * which would stall page-load hydration. Decoding works on a suspended context.
 */
export async function loadUrlIntoChannel(engine, idx, url, name, sourceUrl = url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch audio (${res.status})`);
  const ab = await res.arrayBuffer();
  const buf = await engine.ctx.decodeAudioData(ab);
  engine.loadBufferToChannel(idx, buf, name, sourceUrl);
  return buf;
}
